import { prop } from './prop'
import { AttributeParser } from '../utils/attribute-parser'
import { TypeString } from '../models/type-string'

/**
 * Defines a custom element associated with the decorated class.
 * @param tag The HTML tag to define, typically prefixed with `gb-`.
 */
export function component(tag: string) {
	return function (Ctor: CustomElementConstructor): void {
		prop('boolean')(Ctor.prototype, 'framework')

		const { onInit } = Ctor.prototype

		if (onInit) {
			Ctor.prototype.connectedCallback = function () {
				// Let framework consumers manually run initialization in their lifecycle hooks.
				if (!this.framework) {
					// Depending on how the JS is loaded (sync or async) we either initialize
					// immediately or we need to wait for DOMContentLoaded.
					const readyState = document.readyState

					if (readyState === 'interactive' || readyState === 'complete') {
						this.onInit()
					} else {
						window.addEventListener('DOMContentLoaded', this.onInit.bind(this), { once: true })
					}
				}
			}
		}

		// Use previously defined class if multiple versions of a component's JS are loaded on the same page.
		// Note: This is NOT ideal. We should be looking into solutions for ensuring and
		// facilitating only ONE version of a component loaded on a page at any given time.
		const isDefined = customElements.get(tag)
		if (!isDefined) customElements.define(tag, Ctor)
	}
}

/**
 * Metadata that defines what type an attribute is and what function to call when the attribute changes.
 * Used in `GbdsElement` to work with the `prop` decorator.
 */
export interface PropAttributeMetadata {
	changedFn: string
	type: TypeString
}

/**
 * Base GBDS Component class, extends HTMLElement.
 *
 * Allows for automatic observation of properties in conjunction with the prop decorator.
 * This class uses a static map to contain all observed properties and methods. This works because the
 * `attributeChangedCallback` is calling the provided method from the context of the instance.
 *
 * If you want to have custom `attributeChangedCallback` logic, ensure your method calls `super()`.
 * @example
 * export class SomeComponent extends GbdsElement {
 * 		constructor() {
 * 			super()
 * 		}
 *
 * 		attributeChangedCallback(name: string, oldValue: string, newValue: string) {
 *			super()
 *			// Custom attributeChangedCallback logic goes here
 *		}
 * }
 */
export abstract class GbdsElement extends HTMLElement {
	constructor() {
		super()
	}

	// Set in the prop decorator.
	private static observedProps: Map<string, PropAttributeMetadata> = new Map()

	// Required for custom elements to call `attributeChangedCallback`.
	// Consumers don't need to set this as we get the observed props from
	// the `observedProps` map.
	private static get observedAttributes() {
		return this.observedProps.keys()
	}

	protected attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null): void {
		const observedProps = (this.constructor as typeof GbdsElement).observedProps
		const { changedFn, type } = observedProps.get(name)
		const parsedOldVal = AttributeParser.fromAttribute(oldVal, type)
		const parsedNewVal = AttributeParser.fromAttribute(newVal, type)
		this[changedFn](parsedOldVal, parsedNewVal)
	}
}
